/**
 * Multi-select filter helpers for Patient 360 / list filters where
 * "no selection" and "all options selected" are equivalent (show all results).
 */

/** True when selection is empty or includes every available option value. */
export function isMultiSelectShowAll(
  selectedValues: string[],
  optionValues: string[]
): boolean {
  if (optionValues.length === 0) {
    return selectedValues.length === 0;
  }
  if (selectedValues.length === 0) {
    return true;
  }
  if (selectedValues.length < optionValues.length) {
    return false;
  }
  const selectedSet = new Set(selectedValues);
  return optionValues.every((value) => selectedSet.has(value));
}

/** True when the API should omit this filter param (no narrowing). */
export function shouldOmitMultiSelectFilterParam(
  selectedValues: string[],
  optionValues: string[]
): boolean {
  return isMultiSelectShowAll(selectedValues, optionValues);
}

/** Returns API filter values, or undefined when empty / full selection (no filter). */
export function getMultiSelectApiFilterValues(
  selectedValues: string[],
  optionValues: string[]
): string[] | undefined {
  if (shouldOmitMultiSelectFilterParam(selectedValues, optionValues)) {
    return undefined;
  }
  return selectedValues.length > 0 ? selectedValues : undefined;
}

/** True when the filter is actively narrowing results (not empty, not full selection). */
export function isMultiSelectFilterActive(
  selectedValues: string[],
  optionValues: string[]
): boolean {
  return (
    selectedValues.length > 0 &&
    !shouldOmitMultiSelectFilterParam(selectedValues, optionValues)
  );
}

/** Patient 360 filter option sets (API / UI values). */
export const LEAD_DETAILS_OPPORTUNITY_STATUSES = [
  "OPEN",
  "WON",
  "LOST",
] as const;
/**
 * Patient 360 payor filter — API/query + UI labels.
 * Each segment: first letter uppercase, rest lowercase; multi-word values use hyphens (e.g. Self-Pay).
 */
export const LEAD_DETAILS_PAYOR_TYPE_OPTIONS = [
  { value: "Insurance", label: "Insurance" },
  { value: "Corporate", label: "Corporate" },
  { value: "Self-Pay", label: "Self-Pay" },
] as const;

export const LEAD_DETAILS_PAYOR_TYPE_VALUES =
  LEAD_DETAILS_PAYOR_TYPE_OPTIONS.map((option) => option.value);
/**
 * CAR-2862: Patient 360 Tasks Type filter — individual types grouped as
 * SELF TASK (Reminder, Call Back) and SPOC TASK (Query, Request, Complaint, Follow Up).
 * Replaces the prior collapsed UNIT_POC bucket.
 */
export const LEAD_DETAILS_TASK_TYPE_OPTION_GROUPS = [
  {
    label: "SELF TASK",
    options: [
      { value: "REMINDER", label: "Reminder" },
      { value: "CALLBACK", label: "Call Back" },
    ],
  },
  {
    label: "SPOC TASK",
    options: [
      { value: "QUERY", label: "Query" },
      { value: "REQUEST", label: "Request" },
      { value: "COMPLAINT", label: "Complaint" },
      { value: "FOLLOW_UP", label: "Follow Up" },
    ],
  },
] as const;

export const LEAD_DETAILS_TASK_TYPE_OPTIONS =
  LEAD_DETAILS_TASK_TYPE_OPTION_GROUPS.flatMap((group) => [...group.options]);

export const LEAD_DETAILS_TASK_TYPE_FILTERS = [
  "REMINDER",
  "CALLBACK",
  "QUERY",
  "REQUEST",
  "COMPLAINT",
  "FOLLOW_UP",
] as const;
export const LEAD_DETAILS_TASK_STATUSES = ["OPEN", "COMPLETED"] as const;
export const LEAD_DETAILS_WORK_ORDER_API_TYPES = [
  "REMINDER",
  "CALLBACK",
  "FOLLOW_UP",
  "APPOINTMENT",
  "GENERAL",
] as const;
export const LEAD_DETAILS_ACTIVITY_FILTER_TYPES = [
  "NOTES",
  "TASKS",
  "WORK_ORDERS",
  "DISPOSITIONS",
  "ASSIGNMENTS",
] as const;
/** Overview → Opportunities tab filter option values */
export const OVERVIEW_OPPORTUNITY_STATUS_VALUES = [
  "OPEN",
  "WON",
  "LOST",
] as const;
export const OVERVIEW_OPPORTUNITY_CONNECTIVITY_VALUES = [
  "RNR",
  "DND",
  "CALL_BACK_LATER",
  "TRUE_CONNECT",
] as const;
export const OVERVIEW_OPPORTUNITY_SOURCE_VALUES = [
  "HIS",
  "Meta",
  "Website",
  "Manual",
  "Hexa",
] as const;

/** Overview → Patients tab */
export const OVERVIEW_PATIENT_GENDER_VALUES = [
  "male",
  "female",
  "other",
] as const;

/**
 * Overview → Tasks tab and Task Export share this list (work orders + Unit POC).
 * Empty selection means all statuses.
 */
export const OVERVIEW_TASK_STATUS_OPTIONS = [
  { value: "OPEN", label: "Open" },
  { value: "PENDING", label: "Pending" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "PROCESSED", label: "Processed" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "SNOOZED", label: "Snoozed" },
] as const;

export type OverviewTaskStatusOption =
  (typeof OVERVIEW_TASK_STATUS_OPTIONS)[number];

export const OVERVIEW_TASK_STATUS_VALUES: OverviewTaskStatusOption["value"][] =
  OVERVIEW_TASK_STATUS_OPTIONS.map((option) => option.value);
export const OVERVIEW_TASK_PRIORITY_VALUES = [
  "LOW",
  "MEDIUM",
  "HIGH",
  "URGENT",
] as const;
/**
 * CAR-1220: Overview → Tasks and Task Export share this list so Unit POC
 * Query/Request/Follow-up/Complaint stay selectable in both views. Empty
 * selection means all types (backend unions work orders + unit_poc_queries).
 */
export const OVERVIEW_TASK_TYPE_OPTIONS = [
  { value: "CALLBACK", label: "Callback" },
  { value: "REMINDER", label: "Reminder" },
  { value: "FOLLOW_UP", label: "Follow-up" },
  { value: "QUERY", label: "Query" },
  { value: "REQUEST", label: "Request" },
  { value: "COMPLAINT", label: "Complaint" },
  { value: "ESTIMATE", label: "Estimate" },
  { value: "FEEDBACK", label: "Feedback" },
  { value: "APPOINTMENT", label: "Appointment" },
  { value: "GENERAL", label: "General" },
] as const;

export const OVERVIEW_TASK_TYPE_VALUES = OVERVIEW_TASK_TYPE_OPTIONS.map(
  (option) => option.value
);

export const LEAD_DETAILS_DOCUMENT_CATEGORIES = [
  "Prescription",
  "Lab Report",
  "Radiology",
  "Surgery",
  "Insurance",
  "ID Proof",
  "Consent Form",
  "Discharge Summary",
  "Other",
] as const;
